/**
 * Cisco Subnetting & CIDR Engineering Lab
 * Pure mathematical algorithms for IPv4 bitwise manipulation,
 * CIDR calculations, and hierarchical VLSM allocation.
 */

// Convert dotted-decimal IPv4 string to 32-bit unsigned integer
export function ipToInt(ip) {
  const octets = ip.trim().split('.').map(Number);
  if (octets.length !== 4 || octets.some(o => isNaN(o) || o < 0 || o > 255)) {
    return null;
  }
  return ((octets[0] << 24) >>> 0) + ((octets[1] << 16) >>> 0) + ((octets[2] << 8) >>> 0) + (octets[3] >>> 0);
}

// Convert 32-bit unsigned integer to dotted-decimal IPv4 string
export function intToIp(int) {
  return [
    (int >>> 24) & 255,
    (int >>> 16) & 255,
    (int >>> 8) & 255,
    int & 255
  ].join('.');
}

// Calculate 32-bit subnet mask for a given CIDR prefix (0-32)
export function cidrToMaskInt(cidr) {
  if (cidr === 0) return 0;
  return ((0xFFFFFFFF << (32 - cidr)) >>> 0);
}

export function cidrToMask(cidr) {
  return intToIp(cidrToMaskInt(cidr));
}

// Calculate Cisco Inverted Wildcard Mask for a given CIDR prefix
export function cidrToWildcard(cidr) {
  const maskInt = cidrToMaskInt(cidr);
  const wildcardInt = (~maskInt) >>> 0;
  return intToIp(wildcardInt);
}

// Subnet mask to hexadecimal string (e.g. 0xFFFFFF00)
export function maskToHex(cidr) {
  const maskInt = cidrToMaskInt(cidr);
  return '0x' + maskInt.toString(16).toUpperCase().padStart(8, '0');
}

// IP Classification and RFC 1918 Private status
export function getIpClassAndType(ip) {
  const octets = ip.trim().split('.').map(Number);
  if (octets.length !== 4) return { class: 'Unknown', type: 'Invalid' };
  
  const first = octets[0];
  let ipClass = 'Unknown';
  if (first >= 1 && first <= 126) ipClass = 'Class A';
  else if (first === 127) ipClass = 'Loopback (127.0.0.0/8)';
  else if (first >= 128 && first <= 191) ipClass = 'Class B';
  else if (first >= 192 && first <= 223) ipClass = 'Class C';
  else if (first >= 224 && first <= 239) ipClass = 'Class D (Multicast)';
  else if (first >= 240 && first <= 255) ipClass = 'Class E (Experimental/Reserved)';

  let type = 'Public IPv4';
  if (first === 10) type = 'RFC 1918 Private (10.0.0.0/8)';
  else if (first === 172 && octets[1] >= 16 && octets[1] <= 31) type = 'RFC 1918 Private (172.16.0.0/12)';
  else if (first === 192 && octets[1] === 168) type = 'RFC 1918 Private (192.168.0.0/16)';
  else if (first === 169 && octets[1] === 254) type = 'APIPA / Link-Local (169.254.0.0/16)';
  else if (first === 127) type = 'Loopback';

  return { class: ipClass, type };
}

// Generate full binary octet strings showing network vs host bits
export function getBinaryBreakdown(ip, cidr) {
  const ipVal = ipToInt(ip);
  if (ipVal === null) return [];

  const rawBinary = (ipVal >>> 0).toString(2).padStart(32, '0');
  const octets = [];

  for (let i = 0; i < 4; i++) {
    const octetBits = rawBinary.slice(i * 8, (i + 1) * 8);
    const bitObjs = [];
    for (let b = 0; b < 8; b++) {
      const globalBitIndex = i * 8 + b;
      bitObjs.push({
        bit: octetBits[b],
        isNetwork: globalBitIndex < cidr
      });
    }
    octets.push(bitObjs);
  }
  return octets;
}

// Complete Subnet details for a given IP and CIDR prefix
export function calculateSubnet(ip, cidr) {
  const ipInt = ipToInt(ip);
  if (ipInt === null || cidr < 0 || cidr > 32) {
    return { valid: false, error: 'Invalid IP address or CIDR prefix' };
  }

  const maskInt = cidrToMaskInt(cidr);
  const wildcardInt = (~maskInt) >>> 0;
  const networkInt = (ipInt & maskInt) >>> 0;
  const broadcastInt = (networkInt | wildcardInt) >>> 0;

  const totalAddresses = Math.pow(2, 32 - cidr);
  let usableHosts = 0;
  let firstHostInt = 0;
  let lastHostInt = 0;

  if (cidr === 32) {
    usableHosts = 1;
    firstHostInt = networkInt;
    lastHostInt = networkInt;
  } else if (cidr === 31) {
    // RFC 3021: Point-to-Point links using 31-bit prefixes (2 usable hosts, no broadcast)
    usableHosts = 2;
    firstHostInt = networkInt;
    lastHostInt = broadcastInt;
  } else {
    usableHosts = Math.max(0, totalAddresses - 2);
    firstHostInt = networkInt + 1;
    lastHostInt = broadcastInt - 1;
  }

  const meta = getIpClassAndType(ip);

  return {
    valid: true,
    ip: intToIp(ipInt),
    cidr: Number(cidr),
    networkAddress: intToIp(networkInt),
    broadcastAddress: intToIp(broadcastInt),
    subnetMask: intToIp(maskInt),
    wildcardMask: intToIp(wildcardInt),
    hexMask: maskToHex(cidr),
    firstUsable: intToIp(firstHostInt),
    lastUsable: intToIp(lastHostInt),
    totalAddresses,
    usableHosts,
    hostBits: 32 - cidr,
    networkBits: Number(cidr),
    ipClass: meta.class,
    ipType: meta.type,
    binaryOctets: getBinaryBreakdown(ip, cidr)
  };
}

// Hierarchical Variable Length Subnet Mask (VLSM) Allocation
export function calculateVLSM(majorIp, majorCidr, requirements) {
  const majorNetwork = calculateSubnet(majorIp, majorCidr);
  if (!majorNetwork.valid) {
    return { valid: false, error: 'Invalid Major Network block' };
  }

  const majorNetworkInt = ipToInt(majorNetwork.networkAddress);
  const majorCapacity = majorNetwork.totalAddresses;

  // Clone and sort requirements descending by required host count (Cisco VLSM best practice)
  const sortedReqs = [...requirements]
    .map((r, idx) => ({
      id: r.id || `subnet-${idx + 1}`,
      name: r.name || `Subnet ${idx + 1}`,
      neededHosts: Number(r.neededHosts) || 1
    }))
    .sort((a, b) => b.neededHosts - a.neededHosts);

  let currentPointer = majorNetworkInt;
  let totalAllocatedAddresses = 0;
  const allocatedSubnets = [];
  let overflow = false;

  for (const req of sortedReqs) {
    // Calculate required host bits: 2^(h) - 2 >= neededHosts
    // For point-to-point (2 hosts), h=2 -> /30 (or h=1 -> /31 under RFC 3021)
    let hostBits = 2;
    while (Math.pow(2, hostBits) - 2 < req.neededHosts && hostBits < 32) {
      hostBits++;
    }
    const subnetCidr = 32 - hostBits;
    const blockSize = Math.pow(2, hostBits);
    const usableHosts = blockSize - 2;

    // Align currentPointer to subnet boundary if needed
    const remainder = currentPointer % blockSize;
    if (remainder !== 0) {
      currentPointer += (blockSize - remainder);
    }

    const subNetworkInt = currentPointer;
    const subBroadcastInt = (subNetworkInt + blockSize - 1) >>> 0;

    // Check if this subnet exceeds the major network capacity
    if ((subBroadcastInt >>> 0) > (ipToInt(majorNetwork.broadcastAddress) >>> 0)) {
      overflow = true;
    }

    allocatedSubnets.push({
      id: req.id,
      name: req.name,
      neededHosts: req.neededHosts,
      allocatedHosts: usableHosts,
      totalBlockSize: blockSize,
      wastedHosts: usableHosts - req.neededHosts,
      efficiency: ((req.neededHosts / usableHosts) * 100).toFixed(1) + '%',
      cidr: subnetCidr,
      networkAddress: intToIp(subNetworkInt),
      subnetMask: cidrToMask(subnetCidr),
      wildcardMask: cidrToWildcard(subnetCidr),
      firstUsable: intToIp(subNetworkInt + 1),
      lastUsable: intToIp(subBroadcastInt - 1),
      broadcastAddress: intToIp(subBroadcastInt),
      isOverflow: overflow
    });

    currentPointer = subBroadcastInt + 1;
    totalAllocatedAddresses += blockSize;
  }

  const utilizationPercent = Math.min(100, (totalAllocatedAddresses / majorCapacity) * 100);
  const remainingAddresses = Math.max(0, majorCapacity - totalAllocatedAddresses);

  return {
    valid: true,
    majorNetwork,
    subnets: allocatedSubnets,
    totalAllocatedAddresses,
    remainingAddresses,
    utilizationPercent: utilizationPercent.toFixed(1),
    isOverflow: overflow
  };
}

// Generate the complete CIDR quick reference matrix from /8 to /32
export function generateCIDRMatrix() {
  const matrix = [];
  for (let c = 8; c <= 32; c++) {
    const mask = cidrToMask(c);
    const wildcard = cidrToWildcard(c);
    const total = Math.pow(2, 32 - c);
    let usable = 0;
    if (c === 32) usable = 1;
    else if (c === 31) usable = 2;
    else usable = Math.max(0, total - 2);

    const classCEquivalent = (total / 256).toFixed(total >= 256 ? 0 : 4);

    matrix.push({
      cidr: `/${c}`,
      mask,
      wildcard,
      totalAddresses: total.toLocaleString(),
      usableHosts: usable.toLocaleString(),
      classCEquiv: total >= 256 ? `${classCEquivalent}x /24` : `1/${(256 / total)} of /24`
    });
  }
  return matrix;
}
